import axios from 'axios'
import { createStandaloneToast } from '@chakra-ui/react'
import theme from '../utils/theme/theme'
import handleErrorToast from '../components/common/toast/handleErrorToast'
import eventEmitter from '../utils/functions/eventEmitter'

const { toast } = createStandaloneToast({ theme })

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_ENDPOINT,
  timeout: 120000, // 2 minutes
  paramsSerializer: (params) => {
    const parts: string[] = []
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined) return

      if (Array.isArray(value)) {
        // Filter out null/undefined/empty values
        const validValues = value.filter(v => v !== null && v !== undefined && v !== '')
        if (validValues.length > 0) {
          // Send as comma-separated for django-filter __in lookup
          const encoded = validValues.map(v => encodeURIComponent(String(v))).join(',')
          parts.push(`${encodeURIComponent(key)}=${encoded}`)
        }
      } else {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
      }
    })
    return parts.join('&')
  },
})

// Bridge: allows useAuth hook to inject Auth0's getAccessTokenSilently
let getAccessToken: (() => Promise<string>) | null = null

export const setAccessTokenGetter = (getter: () => Promise<string>) => {
  getAccessToken = getter
}

export const getAccessTokenForFetch = async (): Promise<string | null> => {
  if (!getAccessToken) return null
  try {
    return await getAccessToken()
  } catch {
    return null
  }
}

api.interceptors.request.use(
  async (config) => {
    if (getAccessToken) {
      try {
        const token = await getAccessToken()
        config.headers['Authorization'] = `Bearer ${token}`
      } catch {
        // Token fetch failed — let the request go without auth header.
        // The backend will return 401, and the app will redirect to login.
        if (!sessionStorage.getItem('logging_out')) {
          eventEmitter.emit('logout')
        }
      }
    }

    if (config.method?.toLowerCase() === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now(),
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status

    // Presence is a silent, fire-and-forget background feature (60s heartbeat,
    // config poll). Its callers already swallow failures and the backend
    // self-heals (stale-sweep), so a transient error here must never surface a
    // global toast — a non-developer would otherwise see a spurious
    // "Permission denied" on every heartbeat.
    const url = error?.config?.url ?? ''
    const isSilentBackground = url.includes('user/presence/') || url.includes('user/config/')

    if (status === 401) {
      console.warn('[api] 401 Unauthorized — token may be expired or invalid', error?.config?.url)
    } else if (isSilentBackground) {
      // No toast — swallow quietly (the request's own .catch handles control flow).
    } else if (status === 403) {
      handleErrorToast(toast, 'Permission denied', "You don't have permission to perform this action", {
        id: 'api-403',
      })
    } else if (status === 500) {
      handleErrorToast(toast, 'Server error', 'An unexpected error occurred, please try again', {
        id: 'api-500',
      })
    }

    return Promise.reject(error)
  }
)
